import { useEffect, useMemo } from 'react';
import { useWellbeingReports, useDauReports, useFeedbackReports } from './';
import type { DateRange } from './useDateRangePresets';
import type {
  ProcessedWellbeingData,
  DauReportsResponse,
  FeedbacksReportsResponse
} from '../types/reports';

export interface ReportsDataState {
  wellbeing: {
    data: ProcessedWellbeingData | null;
    loading: boolean;
    error: string | null;
  };
  dau: {
    data: DauReportsResponse | null;
    loading: boolean;
    error: string | null;
  };
  feedback: {
    data: FeedbacksReportsResponse | null;
    loading: boolean;
    error: string | null;
  };
  comparisonFeedback: {
    data: FeedbacksReportsResponse | null;
    loading: boolean;
    error: string | null;
  };
}

export interface ReportsDataActions {
  fetchWellbeing: (params?: { startDay: string; endDay: string }) => Promise<void>;
  fetchDau: (params?: { startDate: string; endDate: string }) => Promise<void>;
  fetchFeedback: (params?: { startDay: string; endDay: string; includeAllVersions?: boolean }) => Promise<void>;
  fetchComparisonFeedback: (params?: { startDay: string; endDay: string; includeAllVersions?: boolean }) => Promise<void>;
  refetchAll: (params: { dateRange: DateRange; compareToRange: DateRange }) => Promise<void>;
  clearDauError: () => void;
}

export interface UseReportsDataResult {
  state: ReportsDataState;
  actions: ReportsDataActions;
  loading: boolean;
  hasErrors: boolean;
  errors: string[];
}

/**
 * Consolidated hook for fetching all reports data
 * Provides unified loading states and error handling
 */
export const useReportsData = (
  compareToRange: DateRange
): UseReportsDataResult => {
  // Primary data hooks
  const {
    data: wellbeingData,
    loading: wellbeingLoading,
    error: wellbeingError,
    fetchReports: fetchWellbeingReports
  } = useWellbeingReports();

  const {
    data: dauData,
    loading: dauLoading,
    error: dauError,
    fetchReports: fetchDauReports,
    clearError: clearDauError
  } = useDauReports();

  const {
    data: feedbackData,
    loading: feedbackLoading,
    error: feedbackError,
    fetchReports: fetchFeedbackReports
  } = useFeedbackReports();

  // Comparison feedback data
  const {
    data: comparisonFeedbackData,
    loading: comparisonFeedbackLoading,
    error: comparisonFeedbackError,
    fetchReports: fetchComparisonFeedbackReports
  } = useFeedbackReports();

  // Auto-fetch comparison feedback when compareToRange changes
  useEffect(() => {
    if (compareToRange.startDay && compareToRange.endDay) {
      fetchComparisonFeedbackReports({
        startDay: compareToRange.startDay,
        endDay: compareToRange.endDay,
        includeAllVersions: true
      });
    }
  }, [compareToRange.startDay, compareToRange.endDay, fetchComparisonFeedbackReports]);

  // Consolidated state
  const state: ReportsDataState = useMemo(() => ({
    wellbeing: {
      data: wellbeingData,
      loading: wellbeingLoading,
      error: wellbeingError
    },
    dau: {
      data: dauData,
      loading: dauLoading,
      error: dauError
    },
    feedback: {
      data: feedbackData,
      loading: feedbackLoading,
      error: feedbackError
    },
    comparisonFeedback: {
      data: comparisonFeedbackData,
      loading: comparisonFeedbackLoading,
      error: comparisonFeedbackError
    }
  }), [
    wellbeingData,
    wellbeingLoading,
    wellbeingError,
    dauData,
    dauLoading,
    dauError,
    feedbackData,
    feedbackLoading,
    feedbackError,
    comparisonFeedbackData,
    comparisonFeedbackLoading,
    comparisonFeedbackError
  ]);

  // Actions
  const actions: ReportsDataActions = useMemo(() => ({
    fetchWellbeing: fetchWellbeingReports,
    fetchDau: fetchDauReports,
    fetchFeedback: fetchFeedbackReports,
    fetchComparisonFeedback: fetchComparisonFeedbackReports,
    refetchAll: async (params: { dateRange: DateRange; compareToRange: DateRange }) => {
      // Use explicit date range parameters to ensure consistency
      await Promise.all([
        fetchWellbeingReports({ startDay: params.dateRange.startDay, endDay: params.dateRange.endDay }),
        fetchDauReports({ startDate: params.dateRange.startDay, endDate: params.dateRange.endDay }),
        fetchFeedbackReports({ startDay: params.dateRange.startDay, endDay: params.dateRange.endDay, includeAllVersions: true }),
        fetchComparisonFeedbackReports({ startDay: params.compareToRange.startDay, endDay: params.compareToRange.endDay, includeAllVersions: true })
      ]);
    },
    clearDauError
  }), [
    fetchWellbeingReports,
    fetchDauReports,
    fetchFeedbackReports,
    fetchComparisonFeedbackReports,
    clearDauError
  ]);

  // Combined loading state
  const loading = wellbeingLoading || dauLoading || feedbackLoading || comparisonFeedbackLoading;

  // Combined error state
  const errors = [wellbeingError, dauError, feedbackError, comparisonFeedbackError].filter(
    (error): error is string => error !== null
  );
  const hasErrors = errors.length > 0;

  return {
    state,
    actions,
    loading,
    hasErrors,
    errors
  };
};
